import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface SacrementRequest {
  id: string;
  userId?: string;
  parishId: string;
  sacramentType: string;
  status: string;
  requestDate?: string;
  scheduledDate?: string;
  celebrationLocation?: string;
  godparent1Name?: string;
  godparent2Name?: string;
  fatherName?: string;
  motherName?: string;
  contactPhone?: string;
  contactEmail?: string;
  recipientName?: string;
  recipientBirthDate?: string;
  notes?: string;
  rejectionReason?: string;
  processedAt?: string;
}

/**
 * Mapping des statuts backend (RequestStatus) vers libellés métier
 * affichables sur les pages mariage/funerailles. Mutualisé pour éviter
 * la divergence entre p80 et p30.
 */
export const SACRAMENT_STATUS_LABEL: Record<string, string> = {
  DRAFT: "Brouillon",
  SUBMITTED: "Soumise",
  IN_REVIEW: "En examen",
  UNDER_REVIEW: "En cours",
  DOCUMENTS_REQUIRED: "Précision demandée",
  APPROVED: "Approuvée",
  SCHEDULED: "Planifiée",
  COMPLETED: "Terminée",
  REJECTED: "Rejetée",
  CANCELLED: "Annulée",
};

export interface SacrementTimelineEntry {
  id?: string;
  status: string;
  date?: string;
  /** Date réelle de l'étape, telle que l'envoie sacrement-service. */
  changedAt?: string;
  comment?: string;
  actor?: string;
}

export function useSacrementRequestsByParish(
  parishId: string | null | undefined,
  opts: { status?: string; type?: string } = {},
) {
  const { status, type } = opts;
  return useQuery({
    queryKey: ["sacrement-requests", "parish", parishId, status, type],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (type) params.append("type", type);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await apiRequest(
        "GET",
        `/api/micro/sacrements/parish/${parishId}${qs}`,
      );
      const data = await res.json();
      return (Array.isArray(data) ? data : data.items ?? []) as SacrementRequest[];
    },
  });
}

/**
 * Demandes de mariage pour une paroisse. Le filtre `type=MARRIAGE` est
 * désormais appliqué côté backend (cf. SacramentRequestResource).
 *
 * Limitation actuelle : le DTO `SacramentRequestResponse` n'expose
 * qu'un seul `recipientName` — les deux conjoints sont concaténés
 * dans ce champ (ex. "Jean KOUAME & Marie ASSI") en attendant une
 * évolution backend (`demandeurs[]` typé homme/femme).
 */
export function useMariageRequests(
  parishId: string | null | undefined,
  opts: { status?: string } = {},
) {
  return useSacrementRequestsByParish(parishId, { ...opts, type: "MARRIAGE" });
}

/**
 * Demandes de sépulture / funérailles pour une paroisse. Filtre
 * `type=SEPULTURE_RECORD` côté backend.
 */
export function useFuneraillesRequests(
  parishId: string | null | undefined,
  opts: { status?: string } = {},
) {
  return useSacrementRequestsByParish(parishId, { ...opts, type: "SEPULTURE_RECORD" });
}

export interface CreateSacrementRequestBody {
  parishId: string;
  sacramentType: string;
  recipientName?: string;
  contactPhone?: string;
  contactEmail?: string;
  celebrationLocation?: string;
  scheduledDate?: string;
  fatherName?: string;
  motherName?: string;
}

/**
 * Crée une nouvelle demande de sacrement (workflow secrétariat). Utilisé
 * par les forms p80 mariage et p30 funerailles. Le backend force le
 * statut initial à DRAFT et fixe userId depuis le JWT.
 */
export function useCreateSacrementRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSacrementRequestBody) => {
      const res = await apiRequest("POST", "/api/micro/sacrements", body);
      return (await res.json()) as SacrementRequest;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sacrement-requests"] }),
  });
}

export function useSacrementRequestTimeline(id: string | null | undefined) {
  return useQuery({
    queryKey: ["sacrement-request", id, "timeline"],
    enabled: !!id,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/micro/sacrements/${id}/timeline`,
      );
      const data = await res.json();
      return (Array.isArray(data) ? data : data.items ?? []) as SacrementTimelineEntry[];
    },
  });
}

/**
 * Gestes de traitement d'une demande par l'équipe de la paroisse (31/10/2026) :
 * prise en charge, précision demandée au fidèle, document délivré, refus motivé.
 * Le fidèle est prévenu par notification à chaque geste.
 */
export type GesteDemande = "prise-en-charge" | "precision" | "delivrer" | "reject";

export function useTraiterDemande() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, geste, message }: { id: string; geste: GesteDemande; message?: string }) => {
      const body =
        geste === "reject"
          ? { newStatus: "REJECTED", comment: message ?? "" }
          : geste === "prise-en-charge"
            ? undefined
            : { message: message ?? "" };
      const res = await apiRequest("PUT", `/api/micro/sacrements/${id}/${geste}`, body);
      return (await res.json()) as SacrementRequest;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["sacrement-requests"] });
      qc.invalidateQueries({ queryKey: ["sacrement-request", v.id, "timeline"] });
    },
  });
}
